import { FC, useState, useCallback } from 'react';
import Link from 'next/link';
import PopupResult from '@stelecom/ui-react/components/popups/popupResult';
import { sendMailForConfirm } from '~/src/api/profile/contacts/sendMailForConfirm';
import { confirmEmail } from '~/src/api/profile/contacts/confirmEmail';
import * as ST from './styled';

interface IProps {
  email: string;
  errorMessage: string;
  showPopup: boolean;
  popupStep: EPopupSteps;
  onClosePopup: () => void;
  contactId: number;
  domain: string;
  accessToken: string;
  setErrorMessage: (message: string) => void;
  setPopupStep: (step: EPopupSteps) => void;
  emailConfirmedHandler: () => void;
}

interface ICodeError {
  status: boolean;
  message: string;
}

export enum EPopupSteps {
  success,
  error,
  addedUnconfirmedEmail,
}

enum EPopupResult {
  error = 'error',
  success = 'success',
}

const SHOW_CODE = 1;

export const Popup: FC<IProps> = ({
  email,
  errorMessage,
  showPopup,
  popupStep,
  onClosePopup,
  contactId,
  domain,
  accessToken,
  setErrorMessage,
  setPopupStep,
  emailConfirmedHandler,
}: IProps) => {
  const [code, setCode] = useState<string>('');
  const [codeError, setCodeError] = useState<ICodeError>({
    status: false,
    message: '',
  });
  const changeCode = useCallback((value: string) => {
    setCode(value);
  }, []);

  const sendCodeAgain = useCallback(async() => {
    const url = location.origin + location.pathname;
    const response = await sendMailForConfirm(
      domain,
      accessToken,
      contactId,
      SHOW_CODE,
      url
    );
    if (!response.isSuccess) {
      setErrorMessage(response.error.message ? response.error.message : '');
      setPopupStep(EPopupSteps.error);
    }
  }, [accessToken, contactId, domain]);

  const closePopupHandler = useCallback(() => {
    setCodeError({
      status: false,
      message: '',
    });
    setCode('');
    onClosePopup();
  }, []);

  const submit = useCallback(async() => {
    const response = await confirmEmail(domain, accessToken, code, contactId);
    if (response.isSuccess) {
      await emailConfirmedHandler();
    } else {
      setCodeError({
        status: true,
        message: response.error.message
          ? response.error.message
          : 'Код указан неверно, попробуйте ввести его еще раз',
      });
    }
  }, [domain, accessToken, code, contactId]);

  switch (popupStep) {
    case EPopupSteps.success:
      return (
        <PopupResult
          isOpen={showPopup}
          onClose={closePopupHandler}
          title='Готово'
          type={EPopupResult.success}
        >
          <ST.Text>
            Теперь уведомления будут приходить вам на почту.
            <Link href='/profile#notices'>
              <a> Настройте уведомления</a>
            </Link>
          </ST.Text>
          <ST.Button onClick={closePopupHandler}>Хорошо</ST.Button>
        </PopupResult>
      );
    case EPopupSteps.error:
      return (
        <PopupResult
          isOpen={showPopup}
          onClose={closePopupHandler}
          title='Произошла ошибка'
          text={errorMessage}
          type={EPopupResult.error}
        >
          <ST.Text>Попробуйте позже</ST.Text>
        </PopupResult>
      );
    case EPopupSteps.addedUnconfirmedEmail:
      return (
        <ST.EmailPopup
          isOpen={showPopup}
          onClose={closePopupHandler}
          data-test='popupAddEmail'
        >
          <ST.Header>
            Мы выслали вам на почту код подтверждения.
            <br />
            Пожалуйста, введите его в поле ниже
          </ST.Header>
          <ST.Body>
            <ST.EmailBlock>
              <ST.EmailInput
                type='email'
                value={email}
                disabled
                data-test='popupEmailInput'
              />
              <ST.EmailNotice>
                На вашу электронную почту отправлено письмо с подтверждением.
                <br />
                Перейдите по ссылке в письме или введите код подтверждения.
              </ST.EmailNotice>
            </ST.EmailBlock>
            <ST.CodeBlock>
               <ST.CodeInput
                onChange={changeCode}
                required={true}
                errorMessage={codeError}
                placeholder='Код из письма'
                data-test='popupCodeInput'
               />
              <ST.ErrorMessage hidden={!codeError.status}>
                {codeError.message}
              </ST.ErrorMessage>
              <ST.SendAgainLink onClick={sendCodeAgain}>
                Отправить письмо повторно
              </ST.SendAgainLink>
            </ST.CodeBlock>
             <ST.Button
              onClick={submit}
              disabled={!code}
              data-test='popupSubmitBtn'
             >
              Отправить
             </ST.Button>
          </ST.Body>
        </ST.EmailPopup>
      );
    default:
      return <></>;
  }
};
